import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Button,
  Snackbar,
  Alert,
  Slide,
  Grid,
  Avatar,
  Typography,
  Box,
  Fade,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Select,
  MenuItem,
  FormControl,
  Tooltip,
} from "@mui/material";
import {
  ContentCopy,
  CalendarMonth,
  Link,
  CheckCircle,
  Info,
  Google,
  CalendarToday,
  Schedule,
  ContactMail,
  PlayCircleOutline,
} from "@mui/icons-material";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import AnseraLogo from "assets/images/Anser.png";
import axios from "axios";
import { useAuth } from "context/AuthContext";
import { FAST_API_BASE_URL } from "constants";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

// Helper function to convert Loom share URLs to embed URLs
const getLoomEmbedUrl = (shareUrl) => {
  if (!shareUrl) return null;
  // Convert share URL to embed URL
  return shareUrl.replace('/share/', '/embed/');
};

// Loom video share URLs from environment variables
const LOOM_SHARE_URLS = {
  'google-calendar': process.env.REACT_APP_GOOGLE_CALENDAR_LOOM_URL,
  'calendly': process.env.REACT_APP_CALENDLY_LOOM_URL,
  'zoho-calendar': process.env.REACT_APP_ZOHO_CALENDAR_LOOM_URL,
};

const DEFAULT_PROVIDER = "google-calendar";

const calendarProviders = [
  {
    id: "google-calendar",
    name: "Google Calendar",
    logo: "https://upload.wikimedia.org/wikipedia/commons/a/a5/Google_Calendar_icon_%282020%29.svg",
    description: "Sync calendar events and schedule meetings seamlessly",
    color: "#219297",
    icon: <Google />,
    hasVideo: true,
  },
  {
    id: "calendly",
    name: "Calendly",
    logo: "https://www.bing.com/th/id/OIP.isWc_EiA6Ms4U-FNJlRwEwHaHa?w=193&h=211&c=8&rs=1&qlt=90&o=6&cb=ucfimg1&pid=3.1&rm=2&ucfimg=1",
    description: "Professional scheduling and meeting management",
    color: "#00A4FF",
    icon: <Schedule />,
    hasVideo: true,
  },
  {
    id: "zoho-calendar",
    name: "Zoho Calendar",
    logo: "https://www.zoho.com/branding/images/zoho-logo.png",
    description: "Appointment scheduling with Zoho integration",
    color: "#E42526",
    icon: <CalendarToday />,
    hasVideo: true,
  },
  {
    id: "contact-form",
    name: "Contact Form",
    logo: "",
    description: "Your contact page or form URL for inquiries",
    color: "#6B46C1",
    icon: <ContactMail />,
    hasVideo: false,
  },
];

const providerInstructions = {
  "google-calendar": [
    {
      title: "Sign in to Google Calendar",
      content: "Use a Google Workspace account to access appointment scheduling features.",
    },
    {
      title: "Create Appointment Schedule",
      content: "On the left panel, click 'Create' → 'Appointment schedule'.",
    },
    {
      title: "Configure Settings",
      content: "Set title, availability, duration, and meeting location preferences.",
    },
    {
      title: "Save & Share",
      content: "Save the schedule, then click 'Share' → 'Copy booking page link'.",
    },
    {
      title: "Paste URL",
      content: "The copied link is your public appointment booking URL.",
    },
  ],
  "calendly": [
    {
      title: "Sign in to Calendly",
      content: "Access your Calendly dashboard.",
    },
    {
      title: "Create Event Type",
      content: "Create a new event type (15 min, 30 min, custom, etc.).",
    },
    {
      title: "Configure Settings",
      content: "Set availability, location, and form questions.",
    },
    {
      title: "Publish & Share",
      content: "Publish the event type and copy the 'Share your link' URL.",
    },
    {
      title: "Paste URL",
      content: "Use the copied Calendly link as your booking URL.",
    },
  ],
  "zoho-calendar": [
    {
      title: "Sign in to Zoho Calendar",
      content: "Access your Zoho Calendar dashboard.",
    },
    {
      title: "Navigate to Settings",
      content: "Right-click the calendar from the left pane of the calendar window and click Edit (or) Go to Settings  > Calendar > My Calendars and click on the calendar",
    },
    {
      title: "Enable permissions",
      content: "Under Sharing and permissions > Public access, enable public access for the calendar.",
    },
    {
      title: "Configure permissions",
      content: "Choose if you want others to View only free/busy status or View event details from the drop-down.",
    },
    {
      title: "Customize interface",
      content: "Click on Customize near the Schedule Appointment option under public access to customize your Schedule Appointment interface.",
    },
       {
      title: "Choose language",
      content: "Choose the desired language from the drop-down and Color for your interface",
    },
      {
      title: "Copy the iframe source URL",
      content: "Copy the src URL from the iframe under the Schedule Appointment section",
    },
           {
      title: "Paste the URL here",
      content: " ",
    },
  ],
  "contact-form": [
    {
      title: "Ensure Accessibility",
      content: "Make sure the page is publicly accessible and not behind a login.",
    },
    {
      title: "Form Functionality",
      content: "Ensure the form is working properly and can receive messages.",
    },
    {
      title: "Paste URL",
      content: "Paste your contact page URL in the field on the left.",
    },
  ],
};

const providerExamples = {
  "google-calendar": "https://calendar.google.com/calendar/appointments/schedules/abc123xyz456\nhttps://calendar.app.google.com/calendar/schedule",
  "calendly": "https://calendly.com/yourname/event\nhttps://calendly.com/yourname/30min",
  "zoho-calendar": "https://calendar.zoho.com/book/yourname\nhttps://calendar.zoho.com/meeting/abc123",
  "contact-form": "https://yourwebsite.com/contact\nhttps://yourwebsite.com/contact-form",
};

const providerPricing = {
  "google-calendar": "Free",
  "calendly": "Free",
  "zoho-calendar": "Included in Zoho Workplace paid plans (Free for basic usage)",
  "contact-form": "Free (Your own website)",
};

const providerPlaceholders = {
  "google-calendar": "Enter your Google Calendar appointment schedule URL",
  "calendly": "Enter your Calendly event URL",
  "zoho-calendar": "Enter your Zoho Calendar booking URL",
  "contact-form": "Enter your contact page/form URL (e.g., https://yourwebsite.com/contact)",
};

const providerLabels = {
  "google-calendar": "Google Calendar Booking URL",
  "calendly": "Calendly Event URL",
  "zoho-calendar": "Zoho Calendar Booking URL",
  "contact-form": "Contact Page/Form URL",
};

const Integrations = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const selectRef = useRef(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  const [currentVideoUrl, setCurrentVideoUrl] = useState("");

  const { currentDomain } = useAuth();
  const domainId = currentDomain?.domain_token;
  const [selectedProvider, setSelectedProvider] = useState("");
  const [calendarUrl, setCalendarUrl] = useState("");
  const [urlError, setUrlError] = useState("");
  const [loading, setLoading] = useState({
    check: true,
    save: false,
    disconnect: false,
    fetch: false
  });
  const [hasExistingConnection, setHasExistingConnection] = useState(false);
  const [existingProvider, setExistingProvider] = useState("");
  const [existingUrl, setExistingUrl] = useState("");
  const [providerHistory, setProviderHistory] = useState({});
  const [shouldFetchProviderDetails, setShouldFetchProviderDetails] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    severity: "success",
    message: "",
  });

  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    action: "",
  });

  // Get the embed URL for the selected provider
  const getEmbedUrlForProvider = (providerId) => {
    const shareUrl = LOOM_SHARE_URLS[providerId];
    if (!shareUrl) return null;
    return getLoomEmbedUrl(shareUrl);
  };

  // Check if provider has video
  const providerHasVideo = (providerId) => {
    return calendarProviders.find(p => p.id === providerId)?.hasVideo && LOOM_SHARE_URLS[providerId];
  };

  const fetchActiveProvider = useCallback(async () => {
    if (!domainId) return;

    try {
      setLoading(prev => ({ ...prev, check: true }));
      const res = await axios.get(
        `${FAST_API_BASE_URL}/api/calendar-providers`,
        {
          headers: { "DOMAIN-TOKEN": domainId },
        }
      );

      console.log("Active provider API response:", res.data);

      if (res?.data?.resultCode === "0" && res.data.result?.provider) {
        const { provider, calendar_url } = res.data.result;
        setHasExistingConnection(true);
        setExistingProvider(provider);
        setExistingUrl(calendar_url);
        setSelectedProvider(provider);
        setCalendarUrl(calendar_url);
        setProviderHistory(prev => ({ ...prev, [provider]: calendar_url }));
        setDropdownOpen(false);
      } else {
        setSelectedProvider(DEFAULT_PROVIDER);
        setHasExistingConnection(false);
        setExistingProvider("");
        setExistingUrl("");
        setDropdownOpen(true);
        
        fetchProviderDetails(DEFAULT_PROVIDER);
      }
    } catch (err) {
      console.error("Failed to fetch active provider:", err);
      setSelectedProvider(DEFAULT_PROVIDER);
      setHasExistingConnection(false);
      setExistingProvider("");
      setExistingUrl("");
      setDropdownOpen(true);
      fetchProviderDetails(DEFAULT_PROVIDER);
    } finally {
      setLoading(prev => ({ ...prev, check: false }));
    }
  }, [domainId]);

  const fetchProviderDetails = async (providerId) => {
    if (!domainId || !providerId) return;

    try {
      setLoading(prev => ({ ...prev, fetch: true }));
      const res = await axios.get(
        `${FAST_API_BASE_URL}/api/calendar-providers/${providerId}`,
        {
          headers: { "DOMAIN-TOKEN": domainId },
        }
      );

      console.log(`Provider ${providerId} details API response:`, res.data);

      if (res?.data?.resultCode === "0" && res.data.result?.calendar_url) {
        setCalendarUrl(res.data.result.calendar_url);
        setProviderHistory(prev => ({ ...prev, [providerId]: res.data.result.calendar_url }));
      } else {
        setCalendarUrl("");
        setProviderHistory(prev => ({ ...prev, [providerId]: "" }));
      }
    } catch (err) {
      console.error(`Failed to fetch provider details for ${providerId}:`, err);
      setCalendarUrl("");
      setProviderHistory(prev => ({ ...prev, [providerId]: "" }));
    } finally {
      setLoading(prev => ({ ...prev, fetch: false }));
    }
  };

  const saveProvider = async () => {
    if (!selectedProvider || !calendarUrl.trim()) {
      setSnackbar({
        open: true,
        severity: "error",
        message: "Please select a provider and enter a valid URL",
      });
      return;
    }

    const error = validateUrl(selectedProvider, calendarUrl);
    if (error) {
      setUrlError(error);
      return;
    }

    setLoading(prev => ({ ...prev, save: true }));
    try {
      const res = await axios.post(
        `${FAST_API_BASE_URL}/api/calendar-providers`,
        {
          provider: selectedProvider,
          calendar_url: calendarUrl.trim(),
        },
        {
          headers: { "DOMAIN-TOKEN": domainId },
        }
      );
      
      console.log("Save provider API response:", res.data);

      if (res?.data?.resultCode === "0") {
        setHasExistingConnection(true);
        setExistingProvider(selectedProvider);
        setExistingUrl(calendarUrl.trim());
        setProviderHistory(prev => ({ ...prev, [selectedProvider]: calendarUrl.trim() }));
        
        setSnackbar({
          open: true,
          severity: "success",
          message: `${calendarProviders.find(p => p.id === selectedProvider)?.name} connected successfully!`,
        });
      } else {
        throw new Error(res?.data?.messages || "Failed to save provider");
      }
    } catch (err) {
      console.error("Failed to save provider:", err);
      setSnackbar({
        open: true,
        severity: "error",
        message: err.response?.data?.messages || "Failed to connect. Please try again.",
      });
    } finally {
      setLoading(prev => ({ ...prev, save: false }));
    }
  };

  const disconnectProvider = async () => {
    setLoading(prev => ({ ...prev, disconnect: true }));
    try {
      const res = await axios.delete(
        `${FAST_API_BASE_URL}/api/calendar-providers`,
        {
          headers: { "DOMAIN-TOKEN": domainId },
          data: { provider: existingProvider },
        }
      );

      console.log("Disconnect provider API response:", res.data);

      if (res?.data?.resultCode === "0") {
        setHasExistingConnection(false);
        setExistingProvider("");
        setExistingUrl("");
        

        setProviderHistory(prev => ({ ...prev, [existingProvider]: "" }));
        
        setSelectedProvider(DEFAULT_PROVIDER);
        setCalendarUrl("");
        setDropdownOpen(true);
        

        fetchProviderDetails(DEFAULT_PROVIDER);
        
        setSnackbar({
          open: true,
          severity: "success",
          message: "Provider disconnected successfully!",
        });
        setConfirmDialog({ open: false, action: "" });
      } else {
        throw new Error(res?.data?.messages || "Failed to disconnect");
      }
    } catch (err) {
      console.error("Failed to disconnect:", err);
      setSnackbar({
        open: true,
        severity: "error",
        message: err.response?.data?.messages || "Failed to disconnect. Please try again.",
      });
    } finally {
      setLoading(prev => ({ ...prev, disconnect: false }));
    }
  };

  // Effects
  useEffect(() => {
    if (domainId) {
      fetchActiveProvider();
    }
  }, [domainId, fetchActiveProvider]);

  useEffect(() => {
    if (selectedProvider && selectedProvider !== existingProvider) {
      fetchProviderDetails(selectedProvider);
    }
  }, [selectedProvider, existingProvider]);

  useEffect(() => {
    // Reset video when provider changes
    setShowVideo(false);
    setCurrentVideoUrl("");
  }, [selectedProvider]);

  const handleProviderChange = (event) => {
    const providerId = event.target.value;
    setSelectedProvider(providerId);
    setUrlError("");
    setDropdownOpen(false);
    setCalendarUrl("");
    setShowVideo(false);
    setCurrentVideoUrl("");
  };

  const handleUrlChange = (e) => {
    const value = e.target.value;
    setCalendarUrl(value);
    if (selectedProvider) {
      const error = validateUrl(selectedProvider, value);
      setUrlError(error);
    }
  };

  const validateUrl = (provider, url) => {
    const trimmedUrl = url.trim().toLowerCase();
    if (!trimmedUrl) {
      return "URL is required";
    }

    try {
      new URL(trimmedUrl);
    } catch (err) {
      return "Please enter a valid URL starting with http:// or https://";
    }

    const providerPatterns = {
      "google-calendar": [
        /calendar\.google\.com/,
        /calendar\.app\.google/
      ],
      "calendly": [
        /calendly\.com/
      ],
      "zoho-calendar": [
        /calendar\.zoho\.com/,
        /zoho\.com\/calendar/
      ],
      "contact-form": []
    };

    const patterns = providerPatterns[provider];
    
    if (patterns && patterns.length > 0) {
      const isValid = patterns.some(pattern => pattern.test(trimmedUrl));
      if (!isValid) {
        const providerName = calendarProviders.find(p => p.id === provider)?.name || provider;
        return `Please enter a valid ${providerName} URL`;
      }
    }

    return "";
  };

  const openProviderWebsite = () => {
    const urls = {
      "google-calendar": "https://calendar.google.com/calendar/u/0/r",
      "calendly": "https://calendly.com",
      "zoho-calendar": "https://calendar.zoho.com",
    };
    if (selectedProvider && urls[selectedProvider]) {
      window.open(urls[selectedProvider], "_blank");
    }
  };

  const handleWatchVideo = () => {
    const embedUrl = getEmbedUrlForProvider(selectedProvider);
    if (embedUrl) {
      setCurrentVideoUrl(embedUrl);
      setShowVideo(true);
      
      // Scroll to video section if on mobile
      if (isMobile) {
        setTimeout(() => {
          document.querySelector('.video-section')?.scrollIntoView({ 
            behavior: 'smooth',
            block: 'center'
          });
        }, 100);
      }
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setSnackbar({
        open: true,
        severity: "info",
        message: "Copied to clipboard!",
      });
    });
  };

  const InstructionStep = ({ number, title, children, icon }) => (
    <Box sx={{ 
      mb: 2,
      display: 'flex',
      gap: 2,
      alignItems: 'flex-start'
    }}>
      <Box
        sx={{
          width: 28,
          height: 28,
          minWidth: 28,
          borderRadius: '50%',
          backgroundColor: '#e0f7fa',
          color: '#219297',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 'bold',
          fontSize: '0.85rem',
          flexShrink: 0,
        }}
      >
        {icon || number}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography 
          variant="subtitle2"
          fontWeight="600" 
          sx={{ 
            color: '#333',
            mb: 0.5,
            fontSize: '0.9rem'
          }}
        >
          {title}
        </Typography>
        <Typography
          sx={{
            color: '#666',
            fontSize: '0.85rem',
            lineHeight: 1.5,
          }}
        >
          {children}
        </Typography>
      </Box>
    </Box>
  );

  const isContactFormSelected = selectedProvider === "contact-form";
  const hasVideo = providerHasVideo(selectedProvider);

  if (loading.check) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <SoftBox
          sx={{
            backgroundColor: "#fff",
            p: 3,
            borderRadius: '16px',
            boxShadow: "0px 2px 12px rgba(0,0,0,0.04)",
            minHeight: "500px",
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress sx={{ color: "#219297", mb: 2 }} />
            <Typography sx={{ color: "#666" }}>Loading integration settings...</Typography>
          </Box>
        </SoftBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox
        sx={{
          backgroundColor: "#fff",
          p: { xs: 2, md: 3 },
          borderRadius: '16px',
          boxShadow: "0px 2px 12px rgba(0,0,0,0.04)",
          minHeight: "500px",
          width: '100%',
        }}
      >
        <Dialog
          open={confirmDialog.open}
          onClose={() => setConfirmDialog({ open: false, action: "" })}
          PaperProps={{
            sx: {
              borderRadius: '12px',
              padding: 1,
              maxWidth: '90vw',
              width: 'min(500px, 90vw)',
              m: 2,
            },
          }}
        >
          <DialogTitle
            sx={{
              fontWeight: "600",
              color: "#219297",
              padding: 2,
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              fontSize: '1.1rem',
            }}
          >
            <Info sx={{ color: "#ff9800", fontSize: 22 }} />
            {confirmDialog.action === "disconnect" ? "Disconnect?" : "Update Connection?"}
          </DialogTitle>
          <DialogContent sx={{ padding: '8px 16px' }}>
            <DialogContentText
              sx={{ 
                color: "#666", 
                fontSize: '0.9rem', 
                lineHeight: 1.6 
              }}
            >
              {confirmDialog.action === "disconnect" 
                ? "Are you sure you want to disconnect? This will remove all integration settings."
                : "You already have a provider connected. Connecting a new one will replace the existing connection."}
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ 
            padding: '12px 16px', 
            gap: 1,
            flexDirection: { xs: 'column', sm: 'row' }
          }}>
            <Button
              onClick={() => setConfirmDialog({ open: false, action: "" })}
              fullWidth={isMobile}
              sx={{
                color: "#666",
                fontWeight: 600,
                textTransform: "none",
                borderRadius: "8px",
                padding: '8px 16px',
                fontSize: '0.9rem',
                "&:hover": {
                  backgroundColor: "#f5f5f5",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={confirmDialog.action === "disconnect" ? disconnectProvider : saveProvider}
              variant="contained"
              disabled={confirmDialog.action === "disconnect" ? loading.disconnect : loading.save}
              fullWidth={isMobile}
              sx={{
                backgroundColor: "#219297",
                fontWeight: 600,
                textTransform: "none",
                color: "#fff",
                borderRadius: "8px",
                padding: '8px 16px',
                fontSize: '0.9rem',
                "&:hover": {
                  backgroundColor: "#1a767a",
                },
              }}
            >
              {confirmDialog.action === "disconnect" 
                ? (loading.disconnect ? "Disconnecting..." : "Disconnect")
                : (loading.save ? "Saving..." : "Replace")}
            </Button>
          </DialogActions>
        </Dialog>
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          TransitionComponent={(props) => <Slide {...props} direction="down" />}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            variant="filled"
            sx={{
              backgroundColor:
                snackbar.severity === "success" ? "#219297" : 
                snackbar.severity === "error" ? "#d32f2f" :
                snackbar.severity === "info" ? "#1976d2" : "#219297",
              color: "#fff",
              borderRadius: '10px',
              fontSize: '0.9rem',
              maxWidth: '90vw',
            }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
        <SoftBox mb={3}>
          <SoftBox display="flex" alignItems="center" gap={2} mb={1}>
            <Box
              sx={{
                borderRadius: '10px',
                p: 0.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <img 
                src={AnseraLogo} 
                alt="Ansera Logo" 
                style={{ 
                  width: '48px',
                  height: 'auto'
                }} 
              />
            </Box>
            <SoftTypography 
              variant="h5"
              fontWeight="bold" 
              color="dark"
              sx={{ fontSize: { xs: '1.25rem', md: '1.5rem' } }}
            >
              Scheduling Integration
            </SoftTypography>
          </SoftBox>
          <SoftTypography
            variant="body2"
            color="text"
            sx={{ 
              opacity: 0.7, 
              fontSize: '0.9rem'
            }}
          >
            Connect your calendar or contact form
          </SoftTypography>
        </SoftBox>
        <Box sx={{ mb: 3 }}>
          <Typography 
            variant="subtitle1" 
            sx={{ 
              mb: 1.5, 
              color: '#219297',
              fontWeight: 600,
              fontSize: '0.95rem'
            }}
          >
            Select Provider
          </Typography>
          
          <FormControl fullWidth>
            <Select
              ref={selectRef}
              id="provider-select"
              value={selectedProvider}
              onChange={handleProviderChange}
              open={dropdownOpen}
              onOpen={() => setDropdownOpen(true)}
              onClose={() => setDropdownOpen(false)}
              disabled={loading.fetch || loading.check}
              displayEmpty
              sx={{
                borderRadius: '10px',
                backgroundColor: '#f8fafa',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#219297',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#1a767a',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#219297',
                  borderWidth: 2,
                },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    borderRadius: '10px',
                    marginTop: '8px',
                    boxShadow: '0px 4px 16px rgba(0,0,0,0.08)',
                    maxHeight: 400,
                  },
                },
              }}
              renderValue={(selected) => {
                if (!selected) {
                  return <em style={{ color: '#666' }}>Select a provider</em>;
                }
                const provider = calendarProviders.find(p => p.id === selected);
                return (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar
                      src={provider.logo}
                      sx={{
                        width: 32,
                        height: 32,
                        backgroundColor: `${provider.color}15`,
                        color: provider.color,
                      }}
                    >
                      {provider.icon}
                    </Avatar>
                    <Typography variant="body1" fontWeight="medium" color="text.primary">
                      {provider.name}
                    </Typography>
                  </Box>
                );
              }}
            >
              <MenuItem value="" disabled>
                <em>Select a provider</em>
              </MenuItem>
              {calendarProviders.map((provider) => (
                <MenuItem key={provider.id} value={provider.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                    <Avatar
                      src={provider.logo}
                      sx={{
                        width: 32,
                        height: 32,
                        backgroundColor: `${provider.color}15`,
                        color: provider.color,
                      }}
                    >
                      {provider.icon}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body1" fontWeight="medium" color="text.primary">
                        {provider.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {provider.description}
                      </Typography>
                    </Box>
                    {hasExistingConnection && existingProvider === provider.id && (
                      <CheckCircle sx={{ color: '#2e7d32', fontSize: 20 }} />
                    )}
                    {loading.fetch && selectedProvider === provider.id && (
                      <CircularProgress size={16} sx={{ color: '#219297' }} />
                    )}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        {selectedProvider && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {hasExistingConnection && existingProvider === selectedProvider && (
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    p: 1.5,
                    backgroundColor: '#e8f5e9',
                    borderRadius: '10px',
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckCircle sx={{ color: '#2e7d32', fontSize: 20 }} />
                      <Typography variant="body2" sx={{ color: '#2e7d32', fontWeight: 500, fontSize: '0.85rem' }}>
                        Connected
                      </Typography>
                    </Box>
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      onClick={() => setConfirmDialog({ open: true, action: "disconnect" })}
                      disabled={loading.disconnect}
                      startIcon={loading.disconnect ? <CircularProgress size={14} /> : null}
                      sx={{ 
                        textTransform: 'none',
                        fontSize: '0.75rem',
                        padding: '4px 12px',
                        minWidth: 'auto',
                        background:"#bd0909ff",
                        color:"white",
                        borderRadius: '6px',
                        '&:hover': {
                          background:"#a00808ff"
                        }
                      }}
                    >
                      {loading.disconnect ? "Disconnecting..." : "Disconnect"}
                    </Button>
                  </Box>
                )}

                <Box>
                  <label 
                    htmlFor="calendar-url"
                    style={{
                      display: 'block',
                      marginBottom: '8px',
                      fontWeight: '600',
                      color: '#219297',
                      fontSize: '0.9rem',
                    }}
                  >
                    {providerLabels[selectedProvider]}
                  </label>
                  <div style={{ position: 'relative' }}>
                    <input
                      id="calendar-url"
                      type="text"
                      value={calendarUrl}
                      onChange={handleUrlChange}
                      placeholder={providerPlaceholders[selectedProvider]}
                      disabled={loading.fetch}
                      style={{
                        width: '100%',
                        padding: '12px',
                        borderRadius: '10px',
                        border: urlError ? '2px solid #d32f2f' : '2px solid #219297',
                        backgroundColor: loading.fetch ? '#f5f5f5' : '#f8fafa',
                        fontSize: '0.9rem',
                        color: '#219297',
                        fontFamily: 'inherit',
                        outline: 'none',
                        transition: 'all 0.2s',
                        boxSizing: 'border-box',
                        opacity: loading.fetch ? 0.7 : 1,
                      }}
                    />
                    {calendarUrl && !loading.fetch && (
                      <button
                        onClick={() => setCalendarUrl("")}
                        style={{
                          position: 'absolute',
                          right: '8px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          color: '#666',
                          padding: '4px 8px',
                          background: 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: '1.5rem',
                          lineHeight: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        ×
                      </button>
                    )}
                    {loading.fetch && (
                      <CircularProgress 
                        size={20} 
                        sx={{ 
                          position: 'absolute', 
                          right: '12px', 
                          top: '50%', 
                          transform: 'translateY(-50%)',
                          color: '#219297'
                        }} 
                      />
                    )}
                  </div>
                  {urlError && (
                    <div style={{ 
                      color: '#d32f2f', 
                      fontSize: '0.8rem', 
                      marginTop: '4px',
                      paddingLeft: '4px'
                    }}>
                      {urlError}
                    </div>
                  )}
                </Box>

                <Box sx={{ 
                  display: "flex", 
                  gap: 1.5,
                  flexDirection: { xs: 'column', sm: 'row' }
                }}>
                  {hasVideo && (
                    <Tooltip title="Watch step-by-step video guide">
                      <Button
                        variant="outlined"
                        onClick={handleWatchVideo}
                        fullWidth={isMobile}
                        startIcon={<PlayCircleOutline />}
                        sx={{
                          borderColor: "#219197",
                          color: "#219197",
                          fontWeight: 600,
                          textTransform: "none",
                          borderRadius: "10px",
                          padding: '10px 20px',
                          fontSize: '0.9rem',
                          flex: 1,
                          minHeight: '44px',
                          "&:hover": {
                            borderColor: "#219197",
                            backgroundColor: "rgba(255, 105, 0, 0.04)",
                          },
                        }}
                      >
                        Watch Video Guide
                      </Button>
                    </Tooltip>
                  )}
                  
                  {!isContactFormSelected && (
                    <Tooltip title={`Open ${calendarProviders.find(p => p.id === selectedProvider)?.name} website`}>
                      <Button
                        variant="outlined"
                        onClick={openProviderWebsite}
                        fullWidth={isMobile}
                        sx={{
                          borderColor: "#219297",
                          color: "#219297",
                          fontWeight: 600,
                          textTransform: "none",
                          borderRadius: "10px",
                          padding: '10px 20px',
                          fontSize: '0.9rem',
                          flex: 1,
                          minHeight: '44px',
                          "&:hover": {
                            borderColor: "#1a767a",
                            backgroundColor: "rgba(33, 146, 151, 0.04)",
                          },
                        }}
                      >
                        Open {calendarProviders.find(p => p.id === selectedProvider)?.name}
                      </Button>
                    </Tooltip>
                  )}
                  
                  <Button
                    variant="contained"
                    onClick={() => {
                      if (hasExistingConnection && existingProvider !== selectedProvider) {
                        setConfirmDialog({ open: true, action: "update" });
                      } else {
                        saveProvider();
                      }
                    }}
                    disabled={loading.save || loading.fetch || !calendarUrl.trim() || urlError}
                    fullWidth={isMobile}
                    sx={{
                      backgroundColor: calendarUrl.trim() && !urlError ? "#219297" : "#ccc",
                      fontWeight: 600,
                      textTransform: "none",
                      color: "#fff",
                      borderRadius: "10px",
                      padding: '10px 20px',
                      fontSize: '0.9rem',
                      flex: isContactFormSelected || !hasVideo ? 1 : 1.5,
                      minHeight: '44px',
                      "&:hover": {
                        backgroundColor: calendarUrl.trim() && !urlError ? "#1a767a" : "#ccc",
                      },
                    }}
                  >
                    {loading.save ? (
                      <>
                        <CircularProgress size={18} sx={{ color: "#fff", mr: 1 }} />
                        Saving...
                      </>
                    ) : hasExistingConnection ? "Update" : "Connect"}
                  </Button>
                </Box>

                {/* Video Player Section */}
                {showVideo && currentVideoUrl && (
                  <Box 
                    className="video-section"
                    sx={{ 
                      mt: 2,
                      animation: 'fadeIn 0.3s ease-in',
                      '@keyframes fadeIn': {
                        from: { opacity: 0, transform: 'translateY(-10px)' },
                        to: { opacity: 1, transform: 'translateY(0)' },
                      }
                    }}
                  >
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      mb: 1.5 
                    }}>
                      <Typography 
                        variant="subtitle1" 
                        sx={{ 
                          color: '#219297',
                          fontWeight: 600,
                          fontSize: '0.95rem',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1
                        }}
                      >
                        <PlayCircleOutline sx={{ color: '#FF6900' }} />
                        {calendarProviders.find(p => p.id === selectedProvider)?.name} Setup Guide
                      </Typography>
                      <Button
                        size="small"
                        onClick={() => setShowVideo(false)}
                        sx={{
                          color: '#666',
                          fontSize: '0.75rem',
                          textTransform: 'none',
                          minWidth: 'auto',
                          padding: '2px 8px'
                        }}
                      >
                        Hide Video
                      </Button>
                    </Box>
                    <Box
                      sx={{
                        borderRadius: "12px",
                        overflow: "hidden",
                        boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.15)",
                        border: "2px solid #e0e0e0",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          boxShadow: "0px 12px 32px rgba(0, 0, 0, 0.2)",
                          borderColor: "#219297",
                        }
                      }}
                    >
                      <div
                        style={{
                          position: "relative",
                          paddingBottom: "62.5%", // 16:10 aspect ratio
                          height: 0,
                          backgroundColor: "#000",
                        }}
                      >
                        <iframe
                          src={currentVideoUrl}
                          title={`${calendarProviders.find(p => p.id === selectedProvider)?.name} Setup Guide`}
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                          }}
                        ></iframe>
                      </div>
                    </Box>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        display: 'block',
                        mt: 1,
                        textAlign: 'center',
                        color: '#666',
                        fontSize: '0.75rem'
                      }}
                    >
                      Complete video walkthrough of the setup process • Powered by Loom
                    </Typography>
                  </Box>
                )}
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  backgroundColor: '#fafafa',
                  p: 2.5,
                  borderRadius: '12px',
                  height: '100%',
                }}
              >
                <Typography
                  variant="h6"
                  fontWeight="600"
                  sx={{
                    color: '#219297',
                    mb: 2,
                    fontSize: '1.05rem',
                  }}
                >
                  Setup Instructions
                </Typography>

                {providerInstructions[selectedProvider]?.map((instruction, index) => (
                  <InstructionStep 
                    key={index}
                    number={index + 1}
                    title={instruction.title}
                  >
                    {instruction.content}
                  </InstructionStep>
                ))}

                {hasVideo && !showVideo && (
                  <Box sx={{ 
                    mt: 2.5, 
                    pt: 2, 
                    borderTop: '1px solid #e0e0e0',
                    display: 'flex',
                    alignItems: "center",
                    gap: 1.5,
                    p: { xs: 1.5, sm: 2 },
                    backgroundColor: "#e0fffaff",
                    borderRadius: "10px",
                    border: "1px solid #45d1d6ff",
                    transition: "all 0.3s ease",
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor: "#c8f4f8ff",
                      transform: "translateY(-2px)",
                      boxShadow: "0px 4px 12px rgba(0, 184, 184, 0.2)",
                    }
                  }}
                  onClick={handleWatchVideo}
                >
                  <PlayCircleOutline sx={{ 
                    color: "#00b8b8ff", 
                    fontSize: { xs: 24, sm: 28 },
                    flexShrink: 0
                  }} />
                  <Box sx={{ flex: 1 }}>
                    <Typography 
                      variant="subtitle2" 
                      fontWeight="600" 
                      sx={{ 
                        color: "#02bbc9ff",
                        fontSize: '0.85rem'
                      }}
                    >
                      Watch Video Guide
                    </Typography>
                    <Typography variant="body2" sx={{ 
                      color: "#02a2afff",
                      fontSize: '0.8rem'
                    }}>
                      {isTablet 
                        ? "Step-by-step video tutorial for setup" 
                        : "Need help? Watch our step-by-step video demonstration"}
                    </Typography>
                  </Box>
                </Box>
                )}

                <Box sx={{ 
                  mt: 2.5, 
                  pt: 2, 
                  borderTop: '1px solid #e0e0e0',
                  ...(hasVideo && !showVideo ? {} : { mt: 0, pt: 0, borderTop: 'none' })
                }}>
                  <Typography variant="subtitle2" fontWeight="600" sx={{ color: '#219297', mb: 0.5, fontSize: '0.85rem' }}>
                    {isContactFormSelected ? "Note:" : "Pricing:"}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#666', fontSize: '0.8rem' }}>
                    {isContactFormSelected 
                      ? "Ensure your contact form is publicly accessible and functional."
                      : providerPricing[selectedProvider]}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        )}
        {!selectedProvider && !hasExistingConnection && !dropdownOpen && (
          <Box
            sx={{
              textAlign: 'center',
              py: 6,
              color: '#999',
            }}
          >
            <CalendarMonth sx={{ fontSize: 48, color: '#e0e0e0', mb: 2 }} />
            <Typography variant="body1" sx={{ mb: 0.5, color: '#666' }}>
              Select a provider to get started
            </Typography>
            <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
              Choose a calendar provider or add your contact form
            </Typography>
          </Box>
        )}

        <style>
          {`
          #calendar-url:focus {
            box-shadow: 0 0 0 3px rgba(33, 146, 151, 0.1);
            border-color: #1a767a;
          }

          #calendar-url::placeholder {
            color: #90a4ae;
            opacity: 0.7;
          }

          @media (max-width: 600px) {
            #calendar-url, 
            input[type="text"],
            input[type="email"],
            input[type="password"] {
              font-size: 16px !important;
            }
            
            iframe {
              width: 100% !important;
              height: auto !important;
            }
          }
          
          @media (max-width: 768px) {
            .video-container {
              position: relative;
              padding-bottom: 56.25%;
              height: 0;
            }
            
            .video-container iframe {
              position: absolute;
              top: 0;
              left: 0;
              width: 100%;
              height: 100%;
            }
          }
        `}
        </style>
      </SoftBox>
    </DashboardLayout>
  );
};

export default Integrations;